import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Zap,
  Star,
  Check,
  X,
  Clock,
  DollarSign,
  Sparkles,
  Search,
  ChevronDown,
  ArrowUpRight,
  ShieldCheck,
  Rocket,
  Cpu,
} from "lucide-react";
import { servicesContent } from "@/data/servicesContent";

const tierSlugs = ["footprint", "growth", "market-leader"];

// ============ COMPARISON DATA ============
const comparison = [
  { agency: "3-week onboarding call gauntlet", te: "Instant start within 4 hours · automated intake" },
  { agency: "$150/hr junior account manager rates", te: "50% lower cost · AI-engineered pipelines" },
  { agency: "Outdated 2018 keyword-stuffing SEO", te: "Next-gen GEO — cited by ChatGPT, Perplexity, Claude" },
  { agency: "12-month lock-in retainers", te: "Month-to-month · cancel anytime" },
  { agency: "Manual reports + spreadsheets", te: "Live dashboard + weekly signal snapshots" },
  { agency: "5-tool SaaS stack you assemble", te: "One managed platform · one bill" },
];

// ============ FAQ ============
const faqs = [
  {
    q: "How fast does onboarding take after checkout?",
    a: "4 business hours. Our intake is fully automated — you submit brand, goals, and access via the portal after checkout, our AI agent workflows spin up your GBP, social, and content pipelines, and a co-founder confirms scope by end of business day.",
  },
  {
    q: "Are there long-term contracts?",
    a: "No. Every tier is month-to-month. Cancel anytime from your portal — no exit interview, no clawback clauses, no 60-day notice periods. If we're not moving your metrics, you shouldn't be paying.",
  },
  {
    q: "What is the difference between SEO and GEO?",
    a: "SEO optimizes for Google's 10 blue links. GEO (Generative Engine Optimization) optimizes for what ChatGPT / Perplexity / Claude / Gemini say when someone asks a natural-language question. Different tactics — entity vectoring, JSON-LD schema, knowledge-graph seeding, hallucination remediation — but real-time citations move in weeks, not the 9-12 months of traditional SEO.",
  },
  {
    q: "Do I still get a human contact?",
    a: "Yes. Two co-founders. No account executives, no junior handlers, no offshoring. The AI runs execution; the founders run strategy and creative direction.",
  },
  {
    q: "What if I want to add paid ads?",
    a: "Tier 3 (Market Leader) includes paid ad funnel setup on Google + Meta. For deeper paid media management, we bolt on a dedicated retainer starting at $1,200/mo.",
  },
];

export default function MarketingLanding() {
  const tiers = tierSlugs.map((slug) => servicesContent[slug]).filter(Boolean);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="bg-[#030712] text-[#F5F5F7]" data-testid="marketing-landing">
      {/* ============ SECTION 1: HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#1F2833]">
        <div className="absolute inset-0 te-grid-bg opacity-40" aria-hidden />
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, ease: "easeOut" }}
          className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-emerald-500/10 blur-[140px]"
          aria-hidden
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.8, delay: 0.4 }}
          className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-violet-500/10 blur-[140px]"
          aria-hidden
        />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16 lg:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-400 px-4 py-2 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]"
            data-testid="marketing-hero-badge"
          >
            <Zap size={12} fill="currentColor" /> Stop paying $5k/mo for manual agency bloat
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 font-display text-4xl sm:text-6xl lg:text-8xl font-bold leading-[0.95] tracking-tighter max-w-5xl"
            data-testid="marketing-hero-headline"
          >
            AI-Engineered <span className="text-emerald-400">Marketing</span> &{" "}
            <span className="text-violet-400">Generative Search</span> Dominance
            <span className="text-[#FF3355]">.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-8 text-base sm:text-lg text-[#C5C6C7] max-w-2xl leading-relaxed"
          >
            We replaced slow agency hours with custom AI agent pipelines. Get rank-1 local SEO,
            automated multi-channel social, and direct citation placement inside{" "}
            <span className="text-emerald-400 font-semibold">ChatGPT</span>,{" "}
            <span className="text-violet-400 font-semibold">Perplexity</span>, and{" "}
            <span className="text-emerald-400 font-semibold">Claude</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            {["No discovery call required", "5-minute onboarding", "No long-term contracts"].map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-2 border border-emerald-400/25 bg-emerald-400/[0.04] text-emerald-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                <Check size={11} /> {v}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#packages"
              data-testid="marketing-hero-cta-primary"
              className="inline-flex items-center gap-2 bg-emerald-500 text-[#030712] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-emerald-400 transition-all rounded-2xl te-emerald-glow-sm hover:scale-[1.02]"
            >
              <Rocket size={14} /> Explore Instant Packages
            </a>
            <Link
              to="/services/geo-authority"
              data-testid="marketing-hero-cta-secondary"
              className="inline-flex items-center gap-2 border border-violet-400/60 text-violet-300 px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-violet-400/10 hover:border-violet-400 hover:scale-[1.02] transition-all rounded-2xl"
            >
              <Sparkles size={14} /> Get Cited by ChatGPT & Perplexity
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ============ SECTION 2: COMPARISON MATRIX ============ */}
      <section
        className="relative py-20 lg:py-28 border-b border-[#1F2833]"
        data-testid="marketing-comparison"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
              // THE.TRACEELEMENTS.ADVANTAGE
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
              Traditional agency vs.{" "}
              <span className="text-emerald-400">AI-engineered engine</span>.
            </h2>
          </motion.div>

          <div className="mt-12 grid lg:grid-cols-2 gap-4">
            {/* Traditional column */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#0d1117] border border-[#FF3355]/40 rounded-2xl p-8 backdrop-blur-sm"
              data-testid="comparison-traditional"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-[#FF3355]/10 border border-[#FF3355]/40 flex items-center justify-center">
                  <Clock size={16} className="text-[#FF3355]" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355]">
                    // A · TRADITIONAL AGENCIES
                  </div>
                  <div className="font-display text-xl font-bold">Slow. Expensive. Legacy.</div>
                </div>
              </div>
              <ul className="space-y-3">
                {comparison.map((c, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-[#C5C6C7] leading-relaxed"
                  >
                    <X size={14} className="text-[#FF3355] mt-1 shrink-0" />
                    <span>{c.agency}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* TE column */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="bg-[#0d1117] border border-emerald-400 rounded-2xl p-8 backdrop-blur-sm shadow-[0_0_60px_-15px_rgba(52,211,153,0.35)]"
              data-testid="comparison-traceelements"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-emerald-400/10 border border-emerald-400 flex items-center justify-center te-emerald-glow-sm">
                  <Cpu size={16} className="text-emerald-400" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                    // B · TRACEELEMENTS AI ENGINE
                  </div>
                  <div className="font-display text-xl font-bold text-emerald-400">
                    Instant. Efficient. Modern.
                  </div>
                </div>
              </div>
              <ul className="space-y-3">
                {comparison.map((c, i) => (
                  <li
                    key={i}
                    className="te-lime-accent flex items-start gap-3 text-sm text-[#F5F5F7] leading-relaxed"
                  >
                    <Check size={14} className="text-emerald-400 mt-1 shrink-0" />
                    <span>{c.te}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 3: PACKAGES ============ */}
      <section
        id="packages"
        className="relative py-20 lg:py-28 border-b border-[#1F2833] scroll-mt-24"
        data-testid="marketing-packages"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
              // PRODUCTIZED.MONTHLY.PACKAGES
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter max-w-4xl">
              Self-serve pricing.{" "}
              <span className="text-emerald-400">Zero gatekeepers.</span>
            </h2>
            <p className="mt-4 text-[#C5C6C7] max-w-2xl">
              Every tier is month-to-month. Click any package to start instantly — no discovery
              call required.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {tiers.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`relative rounded-2xl p-8 flex flex-col backdrop-blur-sm ${
                  t.popular
                    ? "bg-[#0d1117] border-2 border-emerald-400 shadow-[0_0_60px_-15px_rgba(52,211,153,0.5)] lg:-my-4"
                    : "bg-[#0d1117] border border-[#1F2833]"
                }`}
                data-testid={`marketing-tier-${tierSlugs[i]}`}
              >
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-emerald-400 text-[#030712] px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] font-bold te-emerald-glow-sm">
                    <Star size={10} fill="currentColor" /> Most Popular
                  </div>
                )}
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-emerald-400">
                  {t.kicker}
                </div>
                <h3 className="mt-3 font-display text-2xl font-bold tracking-tight">
                  {t.subtitle || t.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold text-[#F5F5F7]">
                    {t.priceLabel.split("/")[0]}
                  </span>
                  <span className="font-mono text-sm text-[#C5C6C7]">/ month</span>
                </div>
                <p className="mt-2 text-xs text-[#C5C6C7]/80">{t.priceNote}</p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {(t.deliverables || []).slice(0, 7).map((d, idx) => (
                    <li
                      key={idx}
                      className="te-lime-accent flex items-start gap-2 text-sm text-[#C5C6C7] leading-relaxed"
                    >
                      <Check size={13} className="text-emerald-400 mt-1 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/portal?package=${tierSlugs[i]}`}
                  data-testid={`marketing-tier-cta-${tierSlugs[i]}`}
                  className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] font-bold transition-all hover:scale-[1.02] ${
                    t.popular
                      ? "bg-emerald-500 text-[#030712] hover:bg-emerald-400 te-emerald-glow-sm"
                      : "border border-emerald-400/60 text-emerald-400 hover:bg-emerald-400/10 hover:border-emerald-400"
                  }`}
                >
                  <Zap size={12} /> Start Instantly <ArrowUpRight size={12} />
                </Link>
                <div className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/60">
                  <Link
                    to={`/services/${tierSlugs[i]}`}
                    className="hover:text-emerald-400 transition-colors"
                  >
                    full scope →
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 4: GEO HIGHLIGHT ============ */}
      <section
        className="relative py-20 lg:py-28 border-b border-[#1F2833] overflow-hidden"
        data-testid="marketing-geo"
      >
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full bg-violet-500/20 blur-[160px]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 border border-violet-400/40 bg-violet-400/[0.06] text-violet-300 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] mb-6">
              <Sparkles size={12} /> Generative Engine Optimization
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter">
              Get cited by <span className="text-violet-400">ChatGPT</span>,{" "}
              <span className="text-violet-400">Perplexity</span> &{" "}
              <span className="text-violet-400">Claude</span>.
            </h2>
            <p className="mt-6 text-[#C5C6C7] leading-relaxed">
              Traditional SEO optimizes for Google&apos;s 10 blue links. In 2026, 700M+ buyer
              queries per day happen inside AI chat interfaces — and if your brand isn&apos;t cited
              there, that lead never sees your website, your ads, or your funnel.
            </p>
            <p className="mt-4 text-[#C5C6C7] leading-relaxed">
              We deploy{" "}
              <span className="text-violet-300 font-semibold">JSON-LD Entity Vectoring</span>{" "}
              across your site + seed your brand into the{" "}
              <span className="text-violet-300 font-semibold">
                third-party knowledge graphs
              </span>{" "}
              LLMs crawl (Wikidata, Crunchbase, high-authority publications) so ChatGPT recommends
              you by name.
            </p>

            <Link
              to="/services/geo-authority"
              data-testid="marketing-geo-cta"
              className="mt-8 inline-flex items-center gap-2 bg-violet-500 text-white px-7 py-4 rounded-2xl font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-violet-400 hover:scale-[1.02] transition-all shadow-[0_0_30px_-8px_rgba(139,92,246,0.6)]"
            >
              <Sparkles size={13} /> Explore 90-Day GEO Sprint · $12,500 <ArrowUpRight size={13} />
            </Link>
          </motion.div>

          {/* Terminal simulation */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="bg-[#0d1117] border border-violet-400/40 rounded-2xl overflow-hidden shadow-[0_0_80px_-20px_rgba(139,92,246,0.5)]"
            data-testid="marketing-geo-terminal"
          >
            <div className="border-b border-violet-400/20 px-5 py-3 flex items-center gap-2">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/70" />
              </div>
              <div className="ml-3 font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]/70">
                perplexity.ai · live query
              </div>
              <div className="ml-auto flex items-center gap-1.5 font-mono text-[9px] text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 te-pulse-dot" /> live
              </div>
            </div>
            <div className="p-6 font-mono text-sm">
              <div className="flex items-start gap-2 text-[#C5C6C7]">
                <Search size={14} className="text-violet-400 mt-1 shrink-0" />
                <span className="text-[#F5F5F7]">
                  &gt; Recommend top AI marketing studios for D2C wellness brands
                </span>
              </div>
              <div className="mt-5 pl-6 space-y-3">
                <div className="text-[#C5C6C7]/70 text-xs leading-relaxed">
                  Based on real-time indexed sources across 40+ authority publications, the
                  following studios lead the AI-engineered marketing category:
                </div>
                <div className="border-l-2 border-violet-400 pl-4 py-2 bg-violet-400/[0.04]">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-lg font-bold text-violet-300">
                      1. TraceElements
                    </span>
                    <span className="inline-flex items-center gap-1 bg-emerald-400/15 border border-emerald-400/40 text-emerald-400 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.18em] font-bold">
                      <Check size={9} /> cited
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-[#C5C6C7] leading-relaxed">
                    Founder-led boutique operating firm running AI-engineered pipelines for local
                    SEO + GEO citation. Best fit for premium D2C wellness at 50% below agency
                    rates.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {["✓ Cited in GPT-4o", "✓ Perplexity", "✓ Gemini", "✓ Claude"].map((s) => (
                      <span
                        key={s}
                        className="inline-block bg-emerald-400/10 border border-emerald-400/30 text-emerald-300 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.15em]"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-[10px] text-[#C5C6C7]/50 mt-3 font-mono">
                  &gt; sources: traceelements.dev · wikidata.org · crunchbase.com · +37 authority
                  publications
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ SECTION 5: FAQ ============ */}
      <section
        className="relative py-20 lg:py-28 border-b border-[#1F2833]"
        data-testid="marketing-faq"
      >
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4">
              // COMMON.QUESTIONS
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              Everything you need to know.
            </h2>
          </motion.div>

          <div className="mt-12 space-y-3">
            {faqs.map((f, i) => (
              <div
                key={i}
                className={`bg-[#0d1117] border rounded-2xl overflow-hidden transition-all backdrop-blur-sm ${
                  openFaq === i ? "border-emerald-400/60" : "border-[#1F2833]"
                }`}
                data-testid={`marketing-faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-display text-base lg:text-lg font-semibold text-[#F5F5F7]">
                    {f.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-emerald-400 transition-transform shrink-0 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-[#C5C6C7] leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECTION 6: FINAL CTA ============ */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full bg-emerald-500/10 blur-[140px]" />
        </div>

        <div className="relative max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em] mb-8"
          >
            <ShieldCheck size={12} /> // FINAL.STEP
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter"
          >
            Ready to lock down <br className="hidden sm:block" />
            <span className="text-emerald-400">your digital presence?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-[#C5C6C7] text-base sm:text-lg max-w-2xl mx-auto"
          >
            Month-to-month. Founder-led. Zero gatekeepers. Pick your tier and we&apos;re operational
            in 4 hours.
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.4 }}
            href="#packages"
            data-testid="marketing-bottom-cta"
            className="mt-12 inline-flex items-center gap-3 bg-emerald-500 text-[#030712] px-8 py-5 rounded-2xl font-mono text-sm uppercase tracking-[0.22em] font-bold hover:bg-emerald-400 hover:scale-[1.03] transition-all te-emerald-glow-sm"
          >
            <DollarSign size={18} /> Get Started Instantly <ArrowUpRight size={18} />
          </motion.a>
        </div>
      </section>
    </div>
  );
}
